import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle } from 'lucide-react';

interface CampaignCancelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reasonType: string, reasonDetails?: string) => void;
  campaignName?: string;
}

const CANCELLATION_REASONS = [
  {
    value: 'position_no_longer_available',
    label: 'Le poste n\'est plus à pourvoir',
    description: 'Le poste a été supprimé ou n\'est plus disponible',
    requiresDetails: false
  },
  {
    value: 'candidate_not_selected',
    label: 'La candidature n\'a pas été retenue',
    description: 'Le candidat n\'a pas été sélectionné pour ce poste',
    requiresDetails: false
  },
  {
    value: 'recruitment_suspended',
    label: 'Process de recrutement suspendu',
    description: 'Le processus de recrutement a été mis en pause ou arrêté',
    requiresDetails: false
  },
  {
    value: 'campaign_error',
    label: 'Erreur dans la campagne',
    description: 'Doublon, mauvais candidat, mauvais poste, etc.',
    requiresDetails: true
  },
  {
    value: 'other',
    label: 'Autre raison',
    description: 'Précisez la raison dans le champ ci-dessous',
    requiresDetails: true
  }
];

export const CampaignCancelDialogWithReason = ({
  open,
  onOpenChange,
  onConfirm,
  campaignName
}: CampaignCancelDialogProps) => {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [reasonDetails, setReasonDetails] = useState<string>('');

  const selectedReasonConfig = CANCELLATION_REASONS.find(r => r.value === selectedReason);
  const showDetailsField = selectedReason && (
    selectedReasonConfig?.requiresDetails || 
    ['recruitment_suspended', 'position_no_longer_available', 'candidate_not_selected'].includes(selectedReason)
  );
  const detailsRequired = selectedReasonConfig?.requiresDetails || false;

  const handleConfirm = () => {
    if (!selectedReason) return;
    if (detailsRequired && !reasonDetails.trim()) return;
    
    onConfirm(selectedReason, reasonDetails || undefined);
    setSelectedReason('');
    setReasonDetails('');
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedReason('');
      setReasonDetails('');
    }
    onOpenChange(newOpen);
  };

  const isConfirmDisabled = !selectedReason || (detailsRequired && !reasonDetails.trim());

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <AlertCircle className="h-6 w-6 text-destructive" />
            <AlertDialogTitle className="text-destructive">
              Annuler la campagne
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-left">
              {campaignName && (
                <p>
                  Vous êtes sur le point d'annuler la campagne
                  <span className="font-medium text-foreground"> "{campaignName}"</span>.
                </p>
              )}
              
              <div className="bg-destructive/10 p-3 rounded-md border border-destructive/20">
                <p className="text-sm font-medium text-destructive mb-2">
                  ⚠️ Cette action enverra des emails :
                </p>
                <ul className="text-xs space-y-1 text-muted-foreground">
                  <li>• Au candidat concerné</li>
                  <li>• Aux référents qui n'ont pas encore répondu</li>
                </ul>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="reason" className="text-sm font-medium">
              Motif d'annulation <span className="text-destructive">*</span>
            </Label>
            <Select value={selectedReason} onValueChange={setSelectedReason}>
              <SelectTrigger id="reason">
                <SelectValue placeholder="Sélectionnez un motif..." />
              </SelectTrigger>
              <SelectContent>
                {CANCELLATION_REASONS.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedReasonConfig && (
              <p className="text-xs text-muted-foreground">
                {selectedReasonConfig.description}
              </p>
            )}
          </div>

          {showDetailsField && (
            <div className="space-y-2">
              <Label htmlFor="details" className="text-sm font-medium">
                Détails supplémentaires {detailsRequired && <span className="text-destructive">*</span>}
                {!detailsRequired && <span className="text-muted-foreground">(optionnel)</span>}
              </Label>
              <Textarea
                id="details"
                placeholder={
                  selectedReason === 'campaign_error' 
                    ? "Précisez le type d'erreur (doublon, mauvais candidat, mauvais poste...)"
                    : "Ajoutez des précisions si nécessaire..."
                }
                value={reasonDetails}
                onChange={(e) => setReasonDetails(e.target.value)}
                rows={3}
                className="resize-none"
              />
              {detailsRequired && !reasonDetails.trim() && (
                <p className="text-xs text-destructive">
                  Ce champ est obligatoire pour ce motif d'annulation
                </p>
              )}
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>
            Annuler
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
          >
            Confirmer l'annulation
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
